import { SearchResultWithVariants } from 'lib/searchspring';
import React, { ReactElement, useEffect, useState, useCallback, useContext } from 'react';
import useIeCheck from 'hooks/useIeCheck';
import CompareButton from 'components/UI/Shop/CompareButton';
import VariantButton from 'components/UI/Shop/VariantButton';
import VtoModal from 'components/UI/Search/VtoModal';
import Link from 'components/UI/Link';
import { ComparisonContext, ComparisonProvider } from 'context/ProductComparisonContext';
import SiteDataContext from 'context/SiteDataContext';
import ProductSearchImage from './ProductSearchImage';
import { getBaseUrl } from 'lib/searchspring/client';
import { buildProductUrl, sortVariants } from 'lib/product';

type ProductResultProps = SearchResultWithVariants & {
  onVtoTrigger: (glassesId: string | false) => void;
  categories: string[];
  position: number;
  locationName?: string;
  locationZip?: string;
};

// https://searchspring.zendesk.com/hc/en-us/articles/201185129-Adding-IntelliSuggest-Tracking#productclicks
export function intellisuggestTrackClick(
  siteId: string,
  href: string,
  intellisuggestData: string,
  intellisuggestSignature: string,
  referrer: string
): boolean {
  const escapeFn = encodeURIComponent || escape;
  const imgTag = new Image();
  const escapedHref = escapeFn(href);
  const esacpedReferer = escapeFn(referrer);
  imgTag.src = `${getBaseUrl(
    siteId
  )}/api/track/track.json?d=${intellisuggestData}&s=${intellisuggestSignature}&u=${escapedHref}&r=${esacpedReferer}`;
  return true;
}

function ProductResult({
  position,
  brand,
  name,
  variants,
  uid,
  categories,
  onVtoTrigger,
  intellisuggestData,
  intellisuggestSignature,
  locationName,
  locationZip,
}: ProductResultProps): ReactElement {
  const [slug, setSlug] = useState('');
  const [primaryImage, setPrimaryImage] = useState('');
  const [variantHasVto, setVariantHasVto] = useState(false);
  const [price, setPrice] = useState('');
  const [current, setCurrent] = useState(0);
  const { selected, addFrame, removeFrame, updateSelection } = useContext(ComparisonContext);
  const [checked, setChecked] = useState(false);
  const isIE11 = useIeCheck();
  const { ecommIds } = useContext(SiteDataContext);

  const vtoEnabled = ecommIds?.fields.enableVto && !isIE11;
  const showPricing = ecommIds?.fields.showPricing;

  const updateCardOptions = useCallback(
    (i: number): void => {
      setCurrent(i);
      const [sku, options] = sortVariants(variants)[i];
      setPrice(options.price);
      setPrimaryImage(options.primary_image || '');
      setVariantHasVto(options.vto);
      const url = buildProductUrl(name, options.color, uid, sku);

      setSlug(url);

      if (checked) {
        const frame = {
          productId: uid,
          sku,
        };

        updateSelection(frame);
      }
    },
    [uid, checked, name, updateSelection, variants]
  );

  const toggleCompare = (state: boolean): void => {
    const [sku] = Object.entries(variants)[current];
    const frame = {
      productId: uid,
      sku,
    };

    state ? addFrame(frame) : removeFrame(frame);
    setChecked(state);
  };

  // initally set image url
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => updateCardOptions(0), []);

  useEffect(() => {
    const initallyChecked = selected?.some((item) => item.productId.toString() === uid);
    setChecked(initallyChecked || false);
  }, [selected, uid]);

  const triggerVto = (): void => {
    onVtoTrigger(Object.entries(variants)[current][1].upc || false);
  };

  return (
    <li className="w-full md:w-1/2 lg:w-1/3 md:hover:shadow-card p-4 group">
      {/* compare */}
      <label className="inline-flex">
        <input
          type="checkbox"
          checked={checked}
          aria-checked={checked}
          aria-label={`${checked ? 'Remove' : 'Add'} to compare list`}
          disabled={selected?.length === 4 && !checked}
          className="h-5 w-5"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleCompare(e.target.checked)}
        />
        <span className="ml-2 text-sm">COMPARE</span>
      </label>

      {/* Image */}
      <div className="flex justify-center mt-2">
        <Link slug={slug} query={locationName && locationZip ? { locationName, locationZip } : {}}>
          <a
            tabIndex={0}
            role="link"
            onMouseDown={() => {
              intellisuggestTrackClick(
                ecommIds?.fields.searchSpringId as string,
                slug,
                intellisuggestData,
                intellisuggestSignature,
                document.referrer
              );
              if (window.dataLayer) {
                window.dataLayer.push({ ecommerce: null });

                const [sku, { color }] = Object.entries(variants)[current];
                window.dataLayer.push({
                  event: 'eec.impressionClick',
                  ecommerce: {
                    click: {
                      actionField: { list: 'Search Results' },
                      products: [
                        {
                          name: name,
                          id: sku,
                          price: price,
                          brand: brand,
                          category: categories.join('>'),
                          variant: color,
                          position: position,
                        },
                      ],
                    },
                  },
                });
              }
            }}
            aria-label={name}
          >
            <ProductSearchImage imageUrl={primaryImage} productName={name} />
          </a>
        </Link>
      </div>

      {/* variant colors */}
      <ul className="flex w-full justify-center mt-2" aria-label="color options">
        {Object.entries(variants)
          .sort((a, b) => ((a[1].color || '') < (b[1].color || '') ? -1 : 1))
          .map(([sku, optionsForSku], i) => (
            <VariantButton
              uid={uid}
              key={sku}
              sku={sku}
              name={name}
              color={optionsForSku.color}
              disableLink
              current={i === current}
              onClick={() => updateCardOptions(i)}
            />
          ))}
      </ul>

      {/* info */}
      <div className="flex flex-col px-1">
        <span className="font-bold">{brand}</span>
        <div className="flex justify-between" style={{ color: '#828282' }}>
          <span>{name}</span>
          {showPricing && <span>${price}</span>}
        </div>
      </div>

      {/* VTO */}
      {vtoEnabled && (
        <button
          disabled={!variantHasVto}
          onClick={triggerVto}
          className={`text-center rounded-full w-full mt-3 py-3 text-sm 
            ${
              variantHasVto
                ? 'md:border md:border-black md:group-hover:border-0 md:group-hover:bg-highlight bg-highlight md:bg-transparent'
                : 'bg-muted opacity-50'
            }`}
        >
          {variantHasVto ? 'Virtual Try-On' : 'Virtual Try-On Coming Soon'}
        </button>
      )}
    </li>
  );
}

export default function ProductResults({
  results,
  categories,
  locationName,
  locationZip,
  siteName,
}: {
  results: SearchResultWithVariants[];
  categories: string[];
  locationName?: string;
  locationZip?: string;
  siteName: string;
}): ReactElement {
  const [vtoGlassesId, setVtoGlassesid] = useState<string | false>(false);

  return (
    <ComparisonProvider>
      <ul
        className={`relative flex flex-wrap md:px-10 lg:px-16 ${
          results?.length !== 0 ? 'md:mt-10' : 'md:mt-6'
        }`}
        aria-label="results list"
      >
        {results.map((result, idx) => {
          return (
            <ProductResult
              {...result}
              key={result.uid}
              categories={categories}
              position={idx}
              onVtoTrigger={(glassesId) => setVtoGlassesid(glassesId || false)}
              locationName={locationName}
              locationZip={locationZip}
            />
          );
        })}

        <CompareButton />
      </ul>
      <VtoModal
        glassesId={vtoGlassesId}
        onClose={() => setVtoGlassesid(false)}
        siteName={siteName}
      />
    </ComparisonProvider>
  );
}
